import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RotateCcw, Check, PenLine } from 'lucide-react';

/**
 * Desktop-friendly signature dialog. Works with mouse, trackpad and touch.
 * Returns { signature_base64, signer_name } via onConfirm.
 */
export const SignatureDialog = ({ open, onOpenChange, onConfirm, initialSignerName = '' }) => {
  const sigRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [signerName, setSignerName] = useState(initialSignerName || '');

  useEffect(() => {
    if (open) {
      setSignerName(initialSignerName || '');
      setIsEmpty(true);
      setTimeout(() => sigRef.current?.clear(), 50);
    }
  }, [open, initialSignerName]);

  const handleClear = () => {
    sigRef.current?.clear();
    setIsEmpty(true);
  };

  const handleConfirm = () => {
    if (!sigRef.current || sigRef.current.isEmpty()) return;
    const canvas = sigRef.current.getCanvas();
    const dataUrl = canvas.toDataURL('image/png');
    const base64 = dataUrl.split(',')[1];
    onConfirm({ signature_base64: base64, signer_name: signerName.trim() });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="desktop-signature-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="h-5 w-5 text-blue-600" />
            Firma del cliente
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="signer-name">Nome firmatario</Label>
            <Input
              id="signer-name"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Nome e cognome del cliente"
              data-testid="desktop-signer-name-input"
            />
          </div>

          <div>
            <Label>Disegna la firma qui sotto (mouse, trackpad o dito)</Label>
            <div className="mt-2 border-2 border-dashed border-slate-300 rounded-lg bg-white overflow-hidden">
              <SignatureCanvas
                ref={sigRef}
                penColor="#0f172a"
                minWidth={1.2}
                maxWidth={2.5}
                throttle={8}
                canvasProps={{
                  width: 620,
                  height: 240,
                  className: 'block w-full h-[240px] cursor-crosshair',
                  'data-testid': 'desktop-signature-canvas',
                }}
                onBegin={() => setIsEmpty(false)}
              />
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              Tieni premuto il pulsante del mouse e disegna la firma.
            </p>
          </div>

          <div className="flex items-center justify-between pt-2">
            <Button
              variant="outline"
              onClick={handleClear}
              disabled={isEmpty}
              data-testid="desktop-signature-clear-btn"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Pulisci
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
              <Button
                onClick={handleConfirm}
                disabled={isEmpty}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="desktop-signature-confirm-btn"
              >
                <Check className="h-4 w-4 mr-2" />
                Conferma firma
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignatureDialog;
